import axios from "axios";
import { toast } from "react-toastify";
import { encrypted, tokenManager } from "../utils/Utility";
import { handleUnauthorized } from "../utils/apiHelper";
import { ENCRYPTION } from "../constant/globalConstants";
import { env } from "../environment";

// Service configurations
const SERVICE_CONFIG = {
  default: {
    baseUrl: env.baseURL,
    tokenKey: "token",
  },
};

/**
 * Determines the service type from options
 */
const getServiceType = (options = {}) => {
  return "default";
};

/**
 * Creates authorization headers for a given service
 */
const createAuthHeaders = (serviceType) => {
  const config = SERVICE_CONFIG[serviceType] || SERVICE_CONFIG.default;
  const token = tokenManager.getToken(config.tokenKey) || localStorage.getItem("token");
  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
};

/**
 * Builds the full URL for a request
 */
const buildUrl = (endpoint, serviceType) => {
  const config = SERVICE_CONFIG[serviceType] || SERVICE_CONFIG.default;
  return `${config.baseUrl}${endpoint}`;
};

/**
 * Encrypts the request body
 */
const encryptBody = (body) => {
  if (!body) return body;
  return { encryptedBody: encrypted.encrypt(typeof body === 'string' ? body : JSON.stringify(body)) };
};

/**
 * Decrypts the response body
 */
const decryptResponse = (data) => {
  if (!data) return data;
  if (data?.encryptedBody) {
    try {
      const decrypted = encrypted.decrypt(data.encryptedBody);
      return typeof decrypted === 'string' ? JSON.parse(decrypted) : decrypted;
    } catch {
      return data.encryptedBody;
    }
  }
  return typeof data === 'string' ? JSON.parse(data) : data;
};

/**
 * Decodes binary error data (ArrayBuffer / Blob) and decrypts encrypted error body if present
 */
const decodeBinaryError = (errorData) => {
  try {
    let text = "";
    if (errorData instanceof ArrayBuffer) {
      const decoder = new TextDecoder("utf-8");
      text = decoder.decode(new Uint8Array(errorData));
    } else if (typeof errorData === "string") {
      text = errorData;
    }
    if (text) {
      const parsed = JSON.parse(text);
      if (parsed?.encryptedBody) {
        const decrypted = encrypted.decrypt(parsed.encryptedBody);
        const decryptedObj = typeof decrypted === "string" ? JSON.parse(decrypted) : decrypted;
        return decryptedObj?.message || decryptedObj?.error || text;
      }
      return parsed?.message || parsed?.error || text;
    }
  } catch {
    // return raw fallback
  }
  return null;
};

/**
 * Formats error messages
 */
const formatErrorMessage = (error) => {
  if (error?.message?.includes("timeout")) {
    return "Request timed out";
  }
  if (error.response?.data) {
    const decodedMsg = decodeBinaryError(error.response.data);
    if (decodedMsg) return decodedMsg;
    if (typeof error.response.data?.message === "string") {
      return error.response.data.message;
    }
  }
  return error.response?.data?.message || error.message || "An error occurred";
};

/**
 * Main axios base query function
 */
export const axiosBaseQuery = async ({
  url,
  method = "GET",
  body = null,
  params = null,
  options = {},
}) => {
  const serviceType = getServiceType(options);
  const fullUrl = buildUrl(url, serviceType);
  const isFormData = body instanceof FormData;

  // Create headers
  const headers = options.skipAuth ? { "Content-Type": "application/json" } : createAuthHeaders(serviceType);

  if (isFormData) {
    delete headers["Content-Type"];
  }

  const source = options?.cancelTokenSource || axios.CancelToken.source();

  const config = {
    headers,
    params: params || {},
    cancelToken: source.token,
  };

  if (options.responseType) {
    config.responseType = options.responseType;
  }

  const timeoutId = setTimeout(() => {
    source.cancel("Request timed out after 15 minutes");
  }, 900000);

  try {
    const enableEncryption = ENCRYPTION;
    const payload = isFormData || !enableEncryption ? body : encryptBody(body);

    const response =
      method.toUpperCase() === "POST"
        ? await axios.post(fullUrl, payload, config)
        : await axios.get(fullUrl, config);

    clearTimeout(timeoutId);

    let data;
    if (options.responseType === 'blob' || options.responseType === 'arraybuffer') {
      data = response.data;
    } else if (response.data?.encryptedBody) {
      data = decryptResponse(response.data);
    } else {
      data = response.data;
    }

    if (data && data.status === false) {
      const error = new Error(data.message || "Operation failed");
      error.status = response.status;
      Object.assign(error, data);
      return { data: null, error };
    }

    return { data, error: null };
  } catch (error) {
    clearTimeout(timeoutId);

    if (axios.isCancel(error)) {
      return { data: null, error, cancelled: true };
    }

    if (error?.response?.status === 401) {
      handleUnauthorized();
    }
    if (error?.response?.status === 504) {
      console.error("Gateway Timeout:", error);
      toast.error("The server is taking too long to respond. Please try again later.");
    }

    const customError = new Error(formatErrorMessage(error));
    customError.status = error?.response?.status;
    customError.originalError = error;
    return { data: null, error: customError };
  }
};

export {
  SERVICE_CONFIG,
  getServiceType,
  buildUrl,
  encryptBody,
  decryptResponse
};
